import React, { useCallback, useState, useEffect } from 'react';
import TextInput from '../common/TextInput';
import Button from '../common/Button';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import { GET_CUSTOMER_USER_DATA } from '../../graphql/queries/customerUser';
import { CREATE_LEGAL_PERSON_REFERENCE } from '../../graphql/mutations/users';

interface AddLegalPersonReferenceProps {
    name: string;
    address: string;
    corporateNumber?: string;
}

const AddLegalPersonReference: React.FC<AddLegalPersonReferenceProps> = ({
    name, address, corporateNumber = ''
}) => {
    const { t } = useTranslation('verification');
    const { data: customerUserData } = useQuery(GET_CUSTOMER_USER_DATA);
    
    const [createLegalPersonReference] = useMutation(CREATE_LEGAL_PERSON_REFERENCE, {
        onCompleted: () => {
            console.log('Legal person reference created successfully');
            window.location.reload();
        },
        onError: (error) => {
            console.error('Error creating legal person reference:', error);
            alert(t('error creating legal person reference'));
        }
    });

    const [corporateName, setCorporateName] = useState<string>('');
    const [corporateAddress, setCorporateAddress] = useState<string>('');
    const [representativeName, setRepresentativeName] = useState<string>('');
    const [representativeNameErr, setRepresentativeNameErr] = useState<string>('');
    const [position, setPosition] = useState<string>('');
    const [name1, setName1] = useState<string>('');
    const [department, setDepartment] = useState<string>('');
    const [emailAddress, setEmailAddress] = useState<string>('');
    const [telephoneNumber, setTelephoneNumber] = useState<string>('');

    useEffect(() => {
        if (customerUserData && customerUserData.getCustomerUserData) {
            setDepartment(customerUserData.getCustomerUserData.division || '');
            setPosition(customerUserData.getCustomerUserData.position || '');
            setName1(customerUserData.getCustomerUserData.name || '');
            setEmailAddress(customerUserData.getCustomerUserData.email || '');
        }
        setCorporateName(name);
        setCorporateAddress(address);
    }, [name, address, customerUserData]);

    const handleCorporateName = useCallback((value: string) => setCorporateName(value), []);
    const handleCorporateAddress = useCallback((value: string) => setCorporateAddress(value), []);
    const handleRepresentativeName = useCallback((value: string) => setRepresentativeName(value), []);
    const handleDepartment = useCallback((value: string) => setDepartment(value), []);
    const handlePosition = useCallback((value: string) => setPosition(value), []);
    const handleName = useCallback((value: string) => setName1(value), []);
    const handleEmailaddress = useCallback((value: string) => setEmailAddress(value), []);
    const handleTelephoneNumber = useCallback((value: string) => setTelephoneNumber(value), []);

    const handleRequiredValidation = useCallback((value: string) => {
        if(value === '') {
            return '必須項目です';
        } else {
            return '';
        }
    }, []);

    const handleAddLegalPersonReference = () => {
        console.log('Reference button clicked');
        
        // Validate representative name
        const representativeNameValidation = handleRequiredValidation(representativeName);
        setRepresentativeNameErr(representativeNameValidation);
        console.log('Representative name validation:', representativeNameValidation);
        
        if (!representativeNameValidation) {
            // Show confirmation dialog
            const confirmed = window.confirm('法人参照を行います。よろしいですか？');
            console.log('Confirmation dialog result:', confirmed);
            
            if (confirmed) {
                console.log('Sending GraphQL request with parameters:', {
                    corporateNumber,
                    corporateName,
                    corporateAddress,
                    representativeName,
                    applicantPhone: telephoneNumber
                });
                
                // Send GraphQL request
                createLegalPersonReference({
                    variables: {
                        corporateNumber,
                        corporateName,
                        corporateAddress,
                        representativeName,
                        applicantPhone: telephoneNumber
                    }
                });
            }
        }
    }

    return (
        <div>
            <TextInput
                label={t('corporate name') + " ※"}
                inputValue={corporateName}
                onChange={handleCorporateName}
                readonly={true}
            />
            <TextInput
                label={t('address') + " ※"}
                inputValue={corporateAddress}
                onChange={handleCorporateAddress}
                readonly={true}
            />
            <TextInput
                label={t('representative name') + " ※"}
                inputValue={representativeName}
                onChange={handleRepresentativeName}
                errorMessage={representativeNameErr}
            />
            <h4>{t('referer information')}</h4>
            <TextInput
                label={t('department') + " ※"}
                inputValue={department}
                onChange={handleDepartment}
                readonly={true}
            />
            <TextInput
                label={t('position') + " ※"}
                inputValue={position}
                onChange={handlePosition}
                readonly={true}
            />
            <TextInput
                label={t('name') + " ※"}
                inputValue={name1}
                onChange={handleName}
                readonly={true}
            />
            <TextInput
                label={t('email address') + " ※"}
                inputValue={emailAddress}
                onChange={handleEmailaddress}
                readonly={true}
            />
            <TextInput
                label={t('telephone number')}
                inputValue={telephoneNumber}
                onChange={handleTelephoneNumber}
            />
            <Button label={t('reference')} onClick={handleAddLegalPersonReference} />
        </div>
    );
};

export default AddLegalPersonReference;
